<script setup lang="ts">
import type { TrainingVideo } from '~/composables/useTraining';
import { isYouTubeUrl, normalizeVideoUrl } from '~/composables/useYouTube';
import TrainingMaterialsList from '~/components/training/MaterialsList.vue';

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
  video: TrainingVideo | null;
  unlocked: boolean;
  /** Academy course navigation (numbered modules only). */
  previousModule?: TrainingVideo | null;
  nextModule?: TrainingVideo | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'markWatched', videoId: string): void;
  (e: 'upgrade'): void;
  (e: 'selectVideo', video: TrainingVideo): void;
}>();

const { width } = useWindowSize();
const isLargeScreen = computed(() => width.value >= 1024);
const { lock, unlock } = useBodyScrollLock();
const player = ref<HTMLVideoElement | null>(null);
const youtubePlayer = ref<HTMLIFrameElement | null>(null);
const hasStarted = ref(false);
const { embedSrc } = useYoutubeCaptions(youtubePlayer, () => youtubeEmbedUrl.value);
const isLocked = ref(false);

const isYouTubeVideo = computed(() => {
  if (!props.video?.videoUrl) return false;
  return isYouTubeUrl(props.video.videoUrl);
});

const youtubeEmbedUrl = computed(() => {
  if (!props.video?.videoUrl || !isYouTubeVideo.value) return '';
  const embed = embedSrc(normalizeVideoUrl(props.video.videoUrl));
  return hasStarted.value ? `${embed}${embed.includes('?') ? '&' : '?'}autoplay=1` : embed;
});

const developerKey = computed(() => {
  const tags = (props.video?.tags || []).map((tag) => tag.toLowerCase());
  return tags.find((tag) => DEV_DOTS[tag]) ?? null;
});

const DEV_DOTS: Record<string, string> = {
  emaar: '#9B1C2E',
  damac: '#D4AF3E',
  sobha: '#0B3D91',
  meraas: '#EA6A2E',
  aldar: '#3B82F6',
  binghatti: '#1F2937',
  nakheel: '#0F766E',
  ellington: '#B45309',
  azizi: '#6D28D9',
};

const developerLabel = computed(() => {
  if (!developerKey.value) return 'Insiders of Dubai';
  return developerKey.value.charAt(0).toUpperCase() + developerKey.value.slice(1);
});

const developerDotColor = computed(() => (developerKey.value ? DEV_DOTS[developerKey.value]! : '#0D0F15'));

const presenterInitials = computed(() => {
  const name = props.video?.presenterName?.trim();
  if (!name) return 'DI';
  const parts = name.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
});

const presenterContacts = computed(() => props.video?.presenterContacts ?? null);

const presenterEmailHref = computed(() => {
  const e = presenterContacts.value?.email?.trim();
  return e ? `mailto:${e}` : null;
});

const presenterPhoneHref = computed(() => {
  const p = presenterContacts.value?.phone?.trim();
  return p ? `tel:${p.replace(/\s+/g, '')}` : null;
});

const presenterWhatsappHref = computed(() => {
  const w = presenterContacts.value?.whatsapp?.trim();
  if (!w) return null;
  const digits = w.replace(/[^\d]/g, '');
  return `https://wa.me/${digits}`;
});

const durationMinutes = computed(() => {
  if (!props.video?.durationSec || props.video.durationSec <= 0) return null;
  return Math.max(1, Math.round(props.video.durationSec / 60));
});

const durationLabel = computed(() => {
  if (durationMinutes.value === null) return null;
  return `${durationMinutes.value}:00`;
});

const durationMetaLabel = computed(() => {
  if (durationMinutes.value === null) return '';
  return t('learn.videoModal.durationMinutes', { count: durationMinutes.value }, durationMinutes.value);
});

const levelKey = computed<'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED'>(() => props.video?.level ?? 'INTERMEDIATE');
const VAULT_LEVEL_LABEL_KEYS: Record<'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED', string> = {
  BEGINNER: 'learn.videoCardV2.levelBeginner',
  INTERMEDIATE: 'learn.videoCardV2.levelIntermediate',
  ADVANCED: 'learn.videoCardV2.levelAdvanced',
};
const levelLabel = computed(() => t(VAULT_LEVEL_LABEL_KEYS[levelKey.value]));
const levelDotClass = computed(() => {
  if (levelKey.value === 'BEGINNER') return 'bg-success';
  if (levelKey.value === 'ADVANCED') return 'bg-black-100';
  return 'bg-gold-primary-100';
});

// Title — emphasize the last word in italic gold (matches design spec).
const titleParts = computed<{ lead: string; accent: string } | null>(() => {
  const title = props.video?.title?.trim();
  if (!title) return null;
  const idx = title.lastIndexOf(' ');
  if (idx < 0) return { lead: '', accent: title };
  return { lead: title.slice(0, idx), accent: title.slice(idx + 1) };
});

const takeaways = computed<string[]>(() => {
  const list = props.video?.takeaways;
  if (!Array.isArray(list)) return [];
  return list.slice(0, 10);
});

// ── Local interaction state (share + saved) ────────────
const shareCopied = ref(false);
const saved = ref(false);
let shareTimeout: ReturnType<typeof setTimeout> | null = null;

const lessonShareUrl = computed(() => {
  if (!process.client || !props.video) return '';
  return `${window.location.origin}/training/${props.video.slug}`;
});

async function shareLesson() {
  if (!process.client || !props.video) return;
  try {
    await navigator.clipboard.writeText(lessonShareUrl.value);
    shareCopied.value = true;
    if (shareTimeout) clearTimeout(shareTimeout);
    shareTimeout = setTimeout(() => {
      shareCopied.value = false;
    }, 2400);
  } catch (err) {
    console.error('Failed to copy share link', err);
  }
}

// TODO(phase-6): persist saved state via /api/training/saved (out of scope here).
function toggleSaved() {
  saved.value = !saved.value;
}

function close() {
  emit('update:modelValue', false);
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      if (!isLocked.value) {
        lock();
        isLocked.value = true;
      }
      hasStarted.value = false;
      saved.value = false;
      shareCopied.value = false;
    } else {
      if (isLocked.value) {
        unlock();
        isLocked.value = false;
      }
      hasStarted.value = false;
      if (player.value) {
        player.value.pause();
        player.value.currentTime = 0;
      }
      if (youtubePlayer.value && isYouTubeVideo.value) {
        youtubePlayer.value.src = youtubePlayer.value.src.replace('&autoplay=1', '').replace('?autoplay=1', '');
      }
    }
  },
);

watch(
  () => props.video,
  () => {
    hasStarted.value = false;
    if (player.value) {
      player.value.pause();
      player.value.currentTime = 0;
    }
  },
);

onUnmounted(() => {
  if (isLocked.value) {
    unlock();
    isLocked.value = false;
  }
  if (shareTimeout) clearTimeout(shareTimeout);
});

function playVideo() {
  if (!props.unlocked) {
    emit('upgrade');
    return;
  }
  if (isYouTubeVideo.value) {
    if (youtubePlayer.value) {
      const src = youtubePlayer.value.src;
      const sep = src.includes('?') ? '&' : '?';
      youtubePlayer.value.src = `${src}${sep}autoplay=1`;
      hasStarted.value = true;
      if (props.video) emit('markWatched', props.video.id);
    }
  } else if (player.value) {
    player.value.play();
  }
}

function onPlay() {
  hasStarted.value = true;
  if (props.video) emit('markWatched', props.video.id);
}

onKeyStroke('Escape', () => {
  if (props.modelValue) close();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="lesson-modal">
      <div
        v-if="modelValue && isLargeScreen && video"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <div
          class="absolute inset-0 bg-black-100/55 backdrop-blur-sm"
          aria-hidden="true"
          @click="close"
        />

        <div
          class="lesson-modal relative z-10 flex w-full max-w-[920px] max-h-[92vh] flex-col overflow-hidden rounded-[22px] bg-gold-primary-10 shadow-[0_50px_120px_rgba(0,0,0,0.45)]"
          @click.stop
        >
          <button
            type="button"
            class="absolute end-4 top-4 z-20 flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25"
            :aria-label="t('learn.videoModal.close')"
            @click="close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
          </button>

          <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div class="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-black-100">
              <iframe
                v-if="unlocked && isYouTubeVideo"
                ref="youtubePlayer"
                :src="youtubeEmbedUrl"
                class="absolute inset-0 h-full w-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
              />
              <video
                v-else-if="unlocked && !isYouTubeVideo"
                ref="player"
                :src="video.videoUrl"
                class="absolute inset-0 h-full w-full bg-black-100 object-cover"
                :class="{ 'opacity-0 pointer-events-none': !hasStarted }"
                :controls="hasStarted"
                :poster="video.thumbnail || undefined"
                playsinline
                preload="metadata"
                dir="ltr"
                @play="onPlay"
              />

              <div
                v-if="!hasStarted || !unlocked"
                class="absolute inset-0"
                style="
                  background:
                    radial-gradient(800px 360px at 50% 35%, rgba(255, 182, 72, 0.28), transparent 65%),
                    linear-gradient(180deg, #1a1610, #0a0b10);
                "
                aria-hidden="true"
              />
              <div
                v-if="!hasStarted || !unlocked"
                class="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 size-[200px] rounded-full"
                style="
                  background: radial-gradient(circle, rgba(255, 182, 72, 0.55), rgba(255, 182, 72, 0) 70%);
                  filter: blur(6px);
                "
                aria-hidden="true"
              />

              <button
                v-if="!hasStarted || !unlocked"
                type="button"
                class="absolute left-1/2 top-1/2 flex size-[76px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-black-100 shadow-[0_10px_40px_rgba(0,0,0,0.35)] transition-transform hover:scale-105"
                :aria-label="t('learn.videoCardV2.watchNow')"
                @click="playVideo"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="ms-1"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>

              <span
                class="absolute start-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-black-100"
              >
                <span
                  class="size-2.5 rounded-[3px]"
                  :style="{ background: developerDotColor }"
                  aria-hidden="true"
                />
                {{ developerLabel }}
              </span>

              <span
                v-if="durationLabel"
                class="absolute bottom-4 end-4 rounded-md bg-black-100/55 px-2.5 py-1 font-mono text-[11px] font-semibold tracking-[0.04em] text-white backdrop-blur"
              >
                {{ durationLabel }}
              </span>

              <div class="absolute bottom-0 start-0 end-0 h-[3px] bg-white/20">
                <div class="h-full w-[28%] bg-gold-primary-100" />
              </div>
            </div>

            <div class="flex flex-col gap-4 p-6 pb-7">
              <div
                class="flex items-center gap-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-black-60"
              >
                <span class="inline-flex items-center gap-1.5">
                  <span
                    :class="['size-1.5 rounded-full', levelDotClass]"
                    aria-hidden="true"
                  />
                  {{ levelLabel }}
                </span>
                <span
                  v-if="durationMetaLabel"
                  class="opacity-30"
                  aria-hidden="true"
                  >·</span
                >
                <span v-if="durationMetaLabel">{{ durationMetaLabel }}</span>
              </div>

              <h2
                v-if="titleParts"
                class="m-0 max-w-[26ch] font-serif text-[30px] font-semibold leading-[1.08] tracking-[-0.02em] text-black-100"
                style="text-wrap: balance"
              >
                <template v-if="titleParts.lead">{{ titleParts.lead }} </template>
                <em class="font-medium italic text-gold-primary-120 not-italic-fix">{{ titleParts.accent }}</em>
              </h2>

              <p class="m-0 max-w-[62ch] text-[14px] leading-[1.6] text-black-70 whitespace-pre-line">
                {{ video.description }}
              </p>

              <div class="flex items-center gap-3.5 rounded-[14px] border border-border-subtle bg-white p-3">
                <span
                  class="relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full font-serif text-[15px] font-semibold text-gold-primary-120"
                  style="
                    background:
                      radial-gradient(30px 30px at 40% 30%, rgba(255, 182, 72, 0.45), transparent 65%),
                      linear-gradient(135deg, #2a1b10, #0d0f15);
                  "
                >
                  <img
                    v-if="video.presenterAvatarUrl"
                    :src="video.presenterAvatarUrl"
                    :alt="video.presenterName ?? ''"
                    class="absolute inset-0 h-full w-full object-cover"
                  />
                  <span
                    v-else
                    class="relative"
                    >{{ presenterInitials }}</span
                  >
                </span>

                <div class="flex flex-1 flex-col gap-px">
                  <span class="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-black-60">
                    {{ t('learn.videoModal.presentedBy') }}
                  </span>
                  <span class="font-serif text-[15px] font-semibold leading-tight tracking-[-0.01em] text-black-100">
                    {{ video.presenterName || 'Insiders of Dubai' }}
                  </span>
                  <span
                    v-if="video.presenterRole"
                    class="font-serif text-[12px] italic text-black-60"
                  >
                    {{ video.presenterRole }}
                  </span>
                </div>

                <div class="flex items-center gap-1.5">
                  <a
                    v-if="presenterEmailHref"
                    :href="presenterEmailHref"
                    class="flex size-[34px] items-center justify-center rounded-[10px] border border-border-subtle bg-black-05 text-black-70 transition-colors hover:border-black-100 hover:bg-black-100 hover:text-white"
                    :aria-label="t('learn.videoModal.emailPresenter')"
                  >
                    <UIIcon
                      name="mail"
                      class="h-4 w-4"
                    />
                  </a>
                  <a
                    v-if="presenterPhoneHref"
                    :href="presenterPhoneHref"
                    class="flex size-[34px] items-center justify-center rounded-[10px] border border-border-subtle bg-black-05 text-black-70 transition-colors hover:border-black-100 hover:bg-black-100 hover:text-white"
                    :aria-label="t('learn.videoModal.callPresenter')"
                  >
                    <UIIcon
                      name="phone"
                      class="h-4 w-4"
                    />
                  </a>
                  <a
                    v-if="presenterWhatsappHref"
                    :href="presenterWhatsappHref"
                    target="_blank"
                    rel="noopener"
                    class="flex size-[34px] items-center justify-center rounded-[10px] border border-border-subtle bg-black-05 text-black-70 transition-colors hover:border-[#25D366] hover:bg-[#25D366] hover:text-white"
                    :aria-label="t('learn.videoModal.whatsappPresenter')"
                  >
                    <UIIcon
                      name="whatsapp"
                      class="h-4 w-4"
                    />
                  </a>
                </div>
              </div>

              <div
                v-if="takeaways.length"
                class="flex flex-col gap-2.5 border-t border-border-subtle pt-3.5"
              >
                <span class="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-black-60">
                  {{ t('learn.videoModal.whatYoullLearn') }}
                </span>
                <ul class="m-0 flex list-none flex-col gap-2 p-0">
                  <li
                    v-for="(item, idx) in takeaways"
                    :key="`${item}-${idx}`"
                    class="flex items-baseline gap-2.5 font-serif text-[15px] font-medium leading-snug text-black-90"
                  >
                    <span
                      class="size-[5px] -translate-y-[3px] rounded-full bg-gold-primary-100"
                      aria-hidden="true"
                    />
                    <span>{{ item }}</span>
                  </li>
                </ul>
              </div>

              <TrainingMaterialsList
                v-if="video"
                :training-id="video.id"
                variant="modal"
                class="border-t border-border-subtle pt-3.5"
                @upgrade="emit('upgrade')"
              />
            </div>
          </div>

          <div class="shrink-0 border-t border-black/[0.08] bg-white px-6 py-4">
            <div class="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                class="inline-flex h-[46px] items-center gap-2.5 rounded-[11px] bg-black-100 px-5 text-[13px] font-bold text-white transition-colors hover:bg-black-90"
                @click="playVideo"
              >
                {{ t('learn.videoCardV2.watchNow') }}
                <span
                  class="font-mono text-[13px] font-medium opacity-80 inline-block rtl:-scale-x-100"
                  aria-hidden="true"
                  >→</span
                >
              </button>
              <button
                type="button"
                class="inline-flex h-[46px] items-center gap-2.5 rounded-[11px] bg-gold-primary-100 px-5 text-[13px] font-bold text-black-100 transition-colors hover:bg-gold-primary-120"
                @click="shareLesson"
              >
                {{ shareCopied ? t('learn.videoModal.linkCopied') : t('learn.videoModal.share') }}
              </button>
              <button
                type="button"
                class="inline-flex h-[46px] items-center gap-2.5 rounded-[11px] border border-border-subtle bg-white px-5 text-[13px] font-bold text-black-100 transition-colors hover:bg-black-03"
                @click="toggleSaved"
              >
                {{ saved ? t('learn.videoModal.saved') : t('learn.videoModal.saveForLater') }}
              </button>
              <div
                v-if="previousModule || nextModule"
                class="ml-auto flex items-center gap-2.5"
              >
                <button
                  v-if="previousModule"
                  type="button"
                  class="inline-flex h-[46px] items-center rounded-[11px] border border-border-subtle bg-white px-4 text-[13px] font-bold text-black-100 transition-colors hover:bg-black-03"
                  @click="emit('selectVideo', previousModule)"
                >
                  <span
                    class="me-1 inline-block rtl:-scale-x-100"
                    aria-hidden="true"
                    >←</span
                  >{{ t('learn.academy.moduleLabel', { n: previousModule.moduleOrder }) }}
                </button>
                <button
                  v-if="nextModule"
                  type="button"
                  class="inline-flex h-[46px] items-center rounded-[11px] bg-black-100 px-4 text-[13px] font-bold text-white transition-colors hover:bg-black-90"
                  @click="emit('selectVideo', nextModule)"
                >
                  {{ t('learn.academy.nextModule', { n: nextModule.moduleOrder })
                  }}<span
                    class="ms-1 inline-block rtl:-scale-x-100"
                    aria-hidden="true"
                    >→</span
                  >
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.lesson-modal-enter-active,
.lesson-modal-leave-active {
  transition: opacity 0.22s ease;
}
.lesson-modal-enter-from,
.lesson-modal-leave-to {
  opacity: 0;
}
.lesson-modal-enter-active .lesson-modal,
.lesson-modal-leave-active .lesson-modal {
  transition:
    transform 0.22s ease,
    opacity 0.22s ease;
}
.lesson-modal-enter-from .lesson-modal,
.lesson-modal-leave-to .lesson-modal {
  opacity: 0;
  transform: scale(0.96);
}

// Italic accent in title — Tailwind `italic` + `not-italic-fix` ensures
// it stays italic even if a parent forces font-style on `em`.
.not-italic-fix {
  font-style: italic;
}
</style>
